import { useState } from 'react'

import type { Exam, MarkingScheme, TaskDraft } from '../../api/exams'
import type { Subject } from '../../api/subjects'
import { Button } from '../../ui/Control'
import { Select } from '../../ui/Select'
import { MARKDOWN } from '../../ui/markup'
import { type ChosenCard, TaskEditor } from './TaskEditor'

/**
 * Setting a test, or changing one.
 *
 * **The whole paper is one act.** Title, filing and the list of tasks are saved together, because
 * that is how somebody works on it: a task added, one moved, the title reworded. A task that keeps
 * its id keeps every answer already written for it in every sitting, which is what makes reordering
 * safe rather than destructive.
 *
 * **The subject is optional and decides nothing.** It files the test and puts its own subject's
 * schemes at the top of every task's picker. A task may still be measured by any scheme of any
 * subject — a test across two subjects is an ordinary thing.
 */
export interface ExamEditorProps {
  /** The test being changed, or `null` while one is being set. */
  readonly exam: Exam | null
  readonly subjects: readonly Subject[]
  readonly schemes: readonly MarkingScheme[]
  readonly onSave: (
    title: string,
    subjectId: string | null,
    tasks: readonly TaskDraft[],
  ) => Promise<void>
  readonly onCancel: () => void
}

/**
 * A task as it is edited, with what the panel needs to draw it kept beside the draft: the
 * document's name where one is chosen, the fixed set's cards where that is the kind — neither is
 * sent when the exam is saved, only the ids inside {@link TaskDraft} are.
 */
interface EditedTask {
  readonly draft: TaskDraft
  readonly documentTitle: string | null
  readonly cards: readonly ChosenCard[]
  /** Only for React's list identity while a task has no id of its own yet. */
  readonly key: string
}

export function ExamEditor({ exam, subjects, schemes, onSave, onCancel }: ExamEditorProps) {
  const [title, setTitle] = useState(exam?.title ?? '')
  const [subjectId, setSubjectId] = useState(exam?.subjectId ?? '')
  const [tasks, setTasks] = useState<readonly EditedTask[]>(() =>
    (exam?.tasks ?? []).map((task) => ({
      key: task.id,
      documentTitle: task.prompt.documentTitle ?? null,
      cards: (task.prompt.cards ?? []).map((card) => ({ id: card.id, label: card.front })),
      draft: {
        id: task.id,
        schemeId: task.schemeId,
        kind: task.prompt.kind,
        documentId: task.prompt.documentId ?? null,
        pages: task.prompt.pages ?? [],
        text: task.prompt.text ?? '',
        markup: task.prompt.markup ?? MARKDOWN,
        cardSetMode: task.prompt.cardSetMode ?? 'fixed',
        cardIds: task.prompt.cardIds ?? [],
        cardQuery: task.prompt.cardFilter?.query ?? '',
        cardTags: task.prompt.cardFilter?.tags ?? [],
        cardTagMode: task.prompt.cardFilter?.tagMode ?? 'all',
      },
    })),
  )
  const [saving, setSaving] = useState(false)

  // The schemes of the test's own subject first, the rest after them. An order and never a filter:
  // a task across two subjects has to stay sayable, so nothing is left out of the list.
  const ordered = [...schemes].sort((left, right) => {
    const mine = (scheme: MarkingScheme) => (scheme.subjectId === subjectId ? 0 : 1)
    return mine(left) - mine(right) || left.subjectName.localeCompare(right.subjectName)
  })

  const addTask = () => {
    const first = ordered[0]
    setTasks([
      ...tasks,
      {
        key: `new-${String(Date.now())}-${String(tasks.length)}`,
        documentTitle: null,
        cards: [],
        draft: {
          schemeId: first?.id ?? '',
          kind: 'written',
          text: '',
          markup: MARKDOWN,
          pages: [],
        },
      },
    ])
  }

  const move = (index: number, by: -1 | 1) => {
    const wanted = index + by
    if (wanted < 0 || wanted >= tasks.length) {
      return
    }
    const moved = [...tasks]
    const [taken] = moved.splice(index, 1)
    if (taken !== undefined) {
      moved.splice(wanted, 0, taken)
    }
    setTasks(moved)
  }

  const save = () => {
    void (async () => {
      setSaving(true)
      try {
        await onSave(
          title,
          subjectId === '' ? null : subjectId,
          tasks.map((task) => task.draft),
        )
      } finally {
        setSaving(false)
      }
    })()
  }

  const nothingToMarkBy = ordered.length === 0

  return (
    <div className="exam-editor">
      <label className="field">
        <span className="field__label">What this test is called</span>
        <input
          className="field__control"
          value={title}
          placeholder="Zwangsvollstreckung, second sitting"
          onChange={(event) => {
            setTitle(event.target.value)
          }}
        />
      </label>

      <Select
        label="Subject"
        value={subjectId}
        options={[
          { value: '', label: 'None' },
          ...subjects.map((subject) => ({ value: subject.id, label: subject.name })),
        ]}
        hint="Files the test, and puts this subject's marking schemes at the top of every task."
        onChange={setSubjectId}
      />

      {nothingToMarkBy ? (
        <p className="exam-editor__note">
          A task is measured by a marking scheme, and marking schemes are written at a subject.
          Write one there first — it says what matters in that kind of task, which sources count,
          and on what scale it is marked.
        </p>
      ) : (
        <ol className="exam-editor__tasks">
          {tasks.map((task, index) => (
            <TaskEditor
              key={task.key}
              draft={task.draft}
              position={index}
              schemes={ordered}
              documentTitle={task.documentTitle}
              cards={task.cards}
              canMoveUp={index > 0}
              canMoveDown={index < tasks.length - 1}
              onMove={(by) => {
                move(index, by)
              }}
              onRemove={() => {
                setTasks(tasks.filter((_, other) => other !== index))
              }}
              onChange={(draft, documentTitle, cards) => {
                setTasks(
                  tasks.map((other, position) =>
                    position === index
                      ? {
                          ...other,
                          draft,
                          documentTitle: documentTitle ?? other.documentTitle,
                          cards: cards ?? other.cards,
                        }
                      : other,
                  ),
                )
              }}
            />
          ))}
        </ol>
      )}

      <div className="controls">
        <Button onClick={addTask} icon="plus" disabled={nothingToMarkBy}>
          One more task
        </Button>
        <Button
          tone="primary"
          onClick={save}
          disabled={saving || title.trim() === '' || tasks.length === 0}
        >
          {exam === null ? 'Create this test' : 'Save'}
        </Button>
        <Button onClick={onCancel}>Leave it</Button>
      </div>
    </div>
  )
}
